import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Chip,
    Card,
    CardContent,
    Avatar,
    IconButton,
    Paper
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SendIcon from '@mui/icons-material/Send';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import CakeIcon from '@mui/icons-material/Cake';
import FlagIcon from '@mui/icons-material/Flag';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SmartLockUnlockButton from '../SmartLockUnlockButton';

export default function ParticipantsList({
    userRole,
    currentUserId,
    attendees,
    handleCheckInAll,
    allCheckedIn,
    isCheckingInAll,
    handleSendMessage,
    handleCheckIn,
    handleOpenDeleteAttendeeConfirm,
    handleOpenProfile,
    getFlagColor,
    setAddAttendeeModalOpen,
    affiliateId,
    classId,
    viewOnly = false,
    isClassListVisible = false,
    isAllClassListVisible = false,
    currentUserClassListNameVisible = false,
}) {
    const isAdmin = !viewOnly && (userRole === "affiliate" || userRole === "trainer");
    const isRegularUser = userRole === 'regular';
    const clubParticipantListEnabled =
        isClassListVisible === true || isAllClassListVisible === true;
    const showNameVisibilityHint =
        isRegularUser &&
        clubParticipantListEnabled &&
        currentUserClassListNameVisible !== true;

    return (
        <>
            {/* Header with actions - only for admins */}
            {isAdmin && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                        flexWrap: 'wrap',
                        gap: 1
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Add Attendee button */}
                        <IconButton
                            color="primary"
                            onClick={() => setAddAttendeeModalOpen(true)}
                            sx={{
                                border: '1px solid',
                                borderColor: 'primary.main',
                                '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                }
                            }}
                        >
                            <PersonAddIcon />
                        </IconButton>

                        {/* Check In All Button */}
                        {attendees.length > 0 && (
                            <Button
                                onClick={handleCheckInAll}
                                disabled={allCheckedIn || isCheckingInAll}
                                variant="contained"
                                color="primary"
                                size="small"
                                startIcon={<HowToRegIcon />}
                                sx={{
                                    backgroundColor: allCheckedIn ? "#4caf50" : "primary.main",
                                    "&:disabled": {
                                        backgroundColor: allCheckedIn ? "#4caf50" : "grey.400",
                                        color: "white",
                                    },
                                }}
                            >
                                {isCheckingInAll ? "Checking in..." : allCheckedIn ? "All Checked In" : "Check In All"}
                            </Button>
                        )}

                        <SmartLockUnlockButton
                            affiliateId={affiliateId}
                            classId={classId}
                            eligibleOnly
                        />
                    </Box>

                    {/* Send Message button */}
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<SendIcon />}
                        onClick={handleSendMessage}
                        disabled={attendees.length === 0}
                    >
                        Message
                    </Button>
                </Box>
            )}

            {/* Participants Section */}
            <Box sx={{ mt: 3, mb: 3 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                        pb: 1,
                        borderBottom: '2px solid',
                        borderColor: 'primary.main'
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 'bold',
                            color: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        <PersonIcon />
                        Registered Participants
                        <Chip
                            label={attendees?.length || 0}
                            size="small"
                            color="primary"
                            sx={{ ml: 1 }}
                        />
                    </Typography>
                </Box>

                {showNameVisibilityHint && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Your name is hidden from other members in this list. If you want your name to be visible, enable{' '}
                        <RouterLink to="/my-profile" style={{ color: 'inherit', fontWeight: 600 }}>
                            Show my name in class participant lists
                        </RouterLink>{' '}
                        under My Profile.
                    </Typography>
                )}

                {attendees?.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {attendees.map((attendee) => {
                            const isCurrentUser = Number(attendee.userId) === Number(currentUserId);
                            const displayedName = (isAdmin || isCurrentUser || attendee.isClassListNameVisible === true
                                ? attendee.fullName
                                : "Athlete") || "Athlete";

                            return (
                            <Card
                                key={attendee.userId}
                                elevation={2}
                                sx={{
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        boxShadow: 4,
                                        transform: 'translateY(-2px)'
                                    },
                                    p: 0
                                }}
                            >
                                <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            gap: 2
                                        }}
                                    >
                                        {/* Left side - User Info */}
                                        <Box sx={{ flex: 1 }}>
                                            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                                <Avatar
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        bgcolor: 'primary.main'
                                                    }}
                                                >
                                                    {(displayedName.charAt(0) || "?").toUpperCase()}
                                                </Avatar>

                                                <Box>
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{
                                                            fontWeight: 'bold',
                                                            cursor: isAdmin ? "pointer" : "default",
                                                            color: "primary.main",
                                                            "&:hover": isAdmin ? { textDecoration: "underline" } : {}
                                                        }}
                                                        onClick={isAdmin ? () => handleOpenProfile(attendee.userId) : undefined}
                                                    >
                                                        {displayedName}
                                                    </Typography>

                                                    {/* Secondary info (only for admins) */}
                                                    {isAdmin && (
                                                        <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                                                            {attendee.isFamilyMember && (
                                                                <Chip
                                                                    label={`Family Member - ${attendee.registrantName}`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="info"
                                                                />
                                                            )}
                                                            {attendee.firstTraining && (
                                                                <Chip
                                                                    label="First Training"
                                                                    size="small"
                                                                    color="success"
                                                                    variant="outlined"
                                                                />
                                                            )}
                                                        </Box>
                                                    )}
                                                </Box>

                                                {/* Icons - Flags and Birthday (only for admins) */}
                                                {isAdmin && (
                                                    <Box display="flex" alignItems="center" gap={0.5}>
                                                        {/* Flags */}
                                                        {attendee.userNotes?.map((note, index) =>
                                                            note.flag ? (
                                                                <FlagIcon
                                                                    key={index}
                                                                    sx={{
                                                                        color: getFlagColor(note.flag),
                                                                        fontSize: 28
                                                                    }}
                                                                />
                                                            ) : null
                                                        )}

                                                        {/* Birthday icon */}
                                                        {attendee.isBirthday && (
                                                            <CakeIcon
                                                                sx={{
                                                                    color: "#FF69B4",
                                                                    fontSize: 28,
                                                                    animation: "pulse 1.5s infinite",
                                                                    "@keyframes pulse": {
                                                                        "0%": { transform: "scale(1)" },
                                                                        "50%": { transform: "scale(1.2)" },
                                                                        "100%": { transform: "scale(1)" }
                                                                    }
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>

                                        {/* Right side - Action buttons (only for admins) */}
                                        {isAdmin && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1
                                                }}
                                            >
                                                <IconButton
                                                    onClick={() => handleCheckIn(attendee)}
                                                    size="medium"
                                                    sx={{
                                                        bgcolor: attendee.checkIn ? "success.main" : "grey.300",
                                                        color: attendee.checkIn ? "white" : "grey.700",
                                                        "&:hover": {
                                                            bgcolor: attendee.checkIn ? "success.dark" : "grey.400",
                                                        },
                                                    }}
                                                >
                                                    <CheckCircleIcon />
                                                </IconButton>

                                                <IconButton
                                                    onClick={() => handleOpenDeleteAttendeeConfirm(attendee)}
                                                    size="medium"
                                                    color="error"
                                                    sx={{
                                                        border: '2px solid',
                                                        borderColor: 'error.main',
                                                        '&:hover': {
                                                            bgcolor: 'error.light',
                                                            color: 'white'
                                                        }
                                                    }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                            );
                        })}
                    </Box>
                ) : (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            textAlign: 'center',
                            bgcolor: 'grey.50',
                            borderRadius: 2
                        }}
                    >
                        <PersonIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                        <Typography variant="body1" color="text.secondary">
                            No participants registered yet
                        </Typography>
                    </Paper>
                )}
            </Box>
        </>
    );
}

